import { useCallback, useEffect, useRef, useState } from "react";

// Open popovers, innermost last. Escape closes only the top one, so a value
// dropdown inside the filter panel closes before the panel itself.
const openStack: symbol[] = [];

/**
 * Open state for an anchored popover. The container wraps both the trigger and
 * the panel, so a click anywhere inside it keeps the popover open.
 */
export function usePopover<T extends HTMLElement = HTMLDivElement>() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<T>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const id = Symbol("popover");
    openStack.push(id);
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && openStack[openStack.length - 1] === id)
        close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      openStack.splice(openStack.indexOf(id), 1);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  return {
    open,
    toggle: () => setOpen((value) => !value),
    close,
    containerRef,
    triggerRef,
  };
}

export const triggerClass =
  "inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:hover:bg-gray-600";

export const sectionLabelClass =
  "mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";
